import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { stats } from "@/data/landing";
import { fadeUp } from "@/lib/motion";
import Aurora from "@/components/landing/effects/Aurora";

const formatNumber = (n) => {
    if (n >= 1000) return n.toLocaleString("en-IN");
    return n.toString();
};

const Counter = ({ to, suffix = "" }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const duration = 1800;
        const start = performance.now();
        let raf;
        const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(to * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, to]);

    return (
        <span ref={ref} className="font-mono tabular-nums">
            {formatNumber(val)}
            {suffix}
        </span>
    );
};

const Stats = () => {
    return (
        <section
            id="stats"
            data-testid="stats-section"
            className="section py-20 md:py-28"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    {...fadeUp}
                    className="glass rounded-3xl p-6 md:p-10 relative overflow-hidden"
                >
                    <Aurora variant="soft" />
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/[0.06] via-transparent to-violet-500/[0.06] pointer-events-none" />

                    <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 rounded-2xl overflow-hidden">
                        {stats.map((s, i) => (
                            <motion.div
                                key={s.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08, duration: 0.6 }}
                                data-testid={`stat-${i}`}
                                className="bg-[#0a0a0c] hover:bg-[#0e0e12] transition px-4 py-7 md:py-8 text-center flex flex-col items-center justify-center min-h-[140px] group"
                            >
                                <div className="font-display font-bold text-white text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem] tracking-tight leading-none group-hover:text-cyan-300 transition whitespace-nowrap">
                                    <Counter to={s.value} suffix={s.suffix} />
                                </div>
                                <div className="mt-3 text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-zinc-500 leading-tight">
                                    {s.label}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Stats;
